import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormField,
  Header,
  Modal,
  RadioGroup,
  RadioGroupProps,
  SpaceBetween,
} from "@amzn/awsui-components-console";

import { JSON_FORMS_ROUTES } from "src/jsonForms/constants";
import { useAppSelector } from "src/redux/hooks";

import AtozEscalationQuestion from "./AtozEscalationQuestion";
import {
  escalationAnswerFromLocationState,
  type AtozEscalationAnswer,
  type AtozEscalationLocationState,
} from "./atozEscalation";
import { userHasLobAccess } from "./lobAccess";
import type { IplPreScreenData, IplQualificationAnswers } from "./types";

interface IplPreScreenProps {
  /**
   * Invoked when the user submits the IPL pre-screen with valid data.
   * Only called when qualification passes.
   */
  onPreScreenSubmit: (data: IplPreScreenData) => void;
}

const YES_NO_OPTIONS: RadioGroupProps.RadioButtonDefinition[] = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

interface QualificationQuestion {
  key: keyof IplQualificationAnswers;
  label: string;
}

const QUALIFICATION_QUESTIONS: QualificationQuestion[] = [
  {
    key: "productCausedInjuries",
    label:
      "Is the claim involving a product that allegedly caused bodily injuries and/or property damages?",
  },
  {
    key: "requestingItemRemoval",
    label: "Is the claimant only requesting that the item be removed from sale?",
  },
  {
    key: "sharingNegativeFeedback",
    label: "Is the claimant only seeking to share their negative feedback?",
  },
  {
    key: "refundRequestOnly",
    label: "Is the claim related to a refund request only?",
  },
  {
    key: "seekingAdditionalCompensation",
    label: "Is the claimant seeking additional compensation for their claim?",
  },
  {
    key: "deliveryIssueRelated",
    label: "Is the claim related to a delivery issue?",
  },
];

const EMPTY_QUALIFICATION_ANSWERS: IplQualificationAnswers = {
  productCausedInjuries: null,
  requestingItemRemoval: null,
  sharingNegativeFeedback: null,
  refundRequestOnly: null,
  seekingAdditionalCompensation: null,
  deliveryIssueRelated: null,
};

/**
 * Returns `true` when the qualification answers represent a valid IPL claim:
 * Q1 (productCausedInjuries) = yes, Q5 (seekingAdditionalCompensation) = yes,
 * and all other questions = no.
 */
function isQualificationValid(answers: IplQualificationAnswers): boolean {
  return (
    answers.productCausedInjuries === "yes" &&
    answers.requestingItemRemoval === "no" &&
    answers.sharingNegativeFeedback === "no" &&
    answers.refundRequestOnly === "no" &&
    answers.seekingAdditionalCompensation === "yes" &&
    answers.deliveryIssueRelated === "no"
  );
}

/**
 * Returns `true` when every qualification question has been answered.
 */
function allQuestionsAnswered(answers: IplQualificationAnswers): boolean {
  return Object.values(answers).every((v) => v !== null);
}

/**
 * IPL pre-screen rendered by `JsonFormsClaimIntakeApp` for the `ipl` LOB.
 *
 * Shows six Yes/No qualification questions. If answers pass validation
 * (Q1=yes, Q5=yes, rest=no) the schema is resolved. Otherwise an
 * invalid-claim modal is displayed.
 *
 * A user who can also file AtoZ claims is first asked whether the claim meets
 * an exclusion that takes it out of the AtoZ program. "No" sends them to the
 * AtoZ pre-screen instead; only "Yes" reveals the qualification questions.
 */
function IplPreScreen({ onPreScreenSubmit }: IplPreScreenProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const userRoles = useAppSelector((state) => state.auth.user.roles);
  // Asked only of users who can reach the AtoZ form that "No" leads to.
  const asksEscalationQuestion = userHasLobAccess(userRoles ?? [], "atoz");

  const [escalationAnswer, setEscalationAnswer] = useState<AtozEscalationAnswer | null>(() =>
    escalationAnswerFromLocationState(location.state)
  );
  const [qualificationAnswers, setQualificationAnswers] = useState<IplQualificationAnswers>(
    EMPTY_QUALIFICATION_ANSWERS
  );
  const [showInvalidModal, setShowInvalidModal] = useState(false);

  const isRedirectedToAtoz = asksEscalationQuestion && escalationAnswer === "atoz";
  const showsQualification = !asksEscalationQuestion || escalationAnswer === "corporate-pl";

  const handleQualificationChange = useCallback(
    (key: keyof IplQualificationAnswers, value: string) => {
      setQualificationAnswers((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSubmit = useCallback(() => {
    if (isRedirectedToAtoz) {
      const state: AtozEscalationLocationState = { atozEscalationAnswer: "atoz" };
      navigate(JSON_FORMS_ROUTES.ATOZ_CLAIM_INTAKE_FORM, { state });
      return;
    }
    if (!showsQualification) {
      return;
    }
    if (isQualificationValid(qualificationAnswers)) {
      onPreScreenSubmit({
        claimPath: "ipl",
        atozCountry: null,
        qualificationAnswers,
      });
    } else {
      setShowInvalidModal(true);
    }
  }, [isRedirectedToAtoz, showsQualification, qualificationAnswers, navigate, onPreScreenSubmit]);

  const isSubmitDisabled = useMemo(
    () =>
      !isRedirectedToAtoz && !(showsQualification && allQuestionsAnswered(qualificationAnswers)),
    [isRedirectedToAtoz, showsQualification, qualificationAnswers]
  );

  return (
    <Box padding="l" data-testid="ipl-pre-screen">
      <SpaceBetween size="m" direction="vertical">
        <Header variant="h2">IPL claim intake</Header>

        {asksEscalationQuestion && (
          <AtozEscalationQuestion
            value={escalationAnswer}
            onChange={setEscalationAnswer}
            testIdPrefix="ipl-pre-screen"
          />
        )}

        {showsQualification && (
          <Container>
            <SpaceBetween size="m" direction="vertical">
              {QUALIFICATION_QUESTIONS.map((q) => (
                <FormField
                  key={q.key}
                  label={q.label}
                  data-testid={`ipl-pre-screen-${q.key}-form-field`}
                >
                  <RadioGroup
                    data-testid={`ipl-pre-screen-${q.key}-radio`}
                    items={YES_NO_OPTIONS}
                    value={qualificationAnswers[q.key]}
                    onChange={({ detail }) => handleQualificationChange(q.key, detail.value)}
                  />
                </FormField>
              ))}
            </SpaceBetween>
          </Container>
        )}

        <Button
          variant="primary"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
          data-testid="ipl-pre-screen-submit-button"
        >
          Continue
        </Button>
      </SpaceBetween>

      <Modal
        visible={showInvalidModal}
        onDismiss={() => setShowInvalidModal(false)}
        header="Invalid IPL Claim"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowInvalidModal(false)}>
                Cancel
              </Button>
            </SpaceBetween>
          </Box>
        }
        data-testid="ipl-pre-screen-invalid-modal"
      >
        <span>
          Based on your answer(s), this does not appear to be a product liability claim. Please
          click cancel and update if you answered in error. Please reach out via this{" "}
          <a
            href="https://t.corp.amazon.com/create/templates/ab7c3b78-7569-4425-bd8f-d54ff377c46e"
            target="_blank"
            rel="noopener noreferrer"
          >
            ticket
          </a>{" "}
          if you need additional assistance.
        </span>
      </Modal>
    </Box>
  );
}

export default IplPreScreen;
